/**
 * Bloodied Edge — Signature Move (utility).
 *
 * Effect: When played, if you have already dealt damage with at least two
 * attacks THIS ROUND, choose two of those damage-dealing attacks. The total
 * damage of the two chosen attacks is queued as a +power buff on your NEXT
 * attack. The buff is consumed by that next attack (standard "next-attack"
 * trigger) and any unused buff expires at round end.
 *
 * Card type: Signature Move with no powerValue / blockValue, so it shows a
 * single purple "Play" button (sig utility window). The engine routes this
 * through playSig(..., mode=null), which now fires onUtilityPlay for
 * utility-mode sigs.
 *
 * Implementation:
 *   - `ctx.self.attacksDealtDamageThisRound` is incremented in resolveCombat
 *     AND in takeDamage each time one of this player's attacks lands
 *     `dmg > 0`. Round-start resets it to 0.
 *   - `ctx.self.damageDealtByAttackThisRound` is the per-attack ledger we
 *     show the player. The prompt lets them pick exactly the two attacks
 *     whose damage should be summed.
 *   - `canPlayUtility` gates the "Play" button so the card cannot even be
 *     played until the precondition is met.
 *
 * After the user confirms the prompt, `confirmBloodiedEdge` runs in
 * playtestEngine.ts to sum the damage and queue the buff.
 */
import type { CardHooks } from "./registry";

export const bloodiedEdge: CardHooks = {
  /**
   * Bloodied Edge is UNPLAYABLE until the player has dealt damage with at
   * least two of their own attacks this round. The ledger tracks both the
   * normal (resolveCombat) path AND the take-damage path, so any combination
   * of "blocked but still dealt damage" + "opponent ate the hit" counts.
   */
  canPlayUtility: (ctx) => {
    const count = ctx.self.attacksDealtDamageThisRound ?? 0;
    const log = ctx.self.damageDealtByAttackThisRound ?? [];
    if (count < 2 || log.length < 2) {
      return {
        enabled: false,
        reason: "Requires two of your attacks to have dealt damage this round.",
      };
    }
    return { enabled: true };
  },
  onUtilityPlay: (ctx) => {
    const log = ctx.self.damageDealtByAttackThisRound ?? [];
    // Defensive fallback — canPlayUtility should have already blocked this,
    // but if something bypasses the gate we silently no-op.
    if ((ctx.self.attacksDealtDamageThisRound ?? 0) < 2 || log.length < 2) {
      return {
        log: [
          `${ctx.card.name} — no bonus (need 2 damage-dealing attacks this round).`,
        ],
      };
    }

    return {
      prompt: {
        kind: "pick-damaging-attacks",
        playerNum: ctx.self.num,
        attacks: log.map((entry) => ({
          slotId: entry.slotId,
          attackCardName: entry.attackCardName,
          damage: entry.damage,
          displayLabel: `${entry.attackCardName} (${entry.damage} dmg)`,
        })),
        maxSelectable: 2,
        selectedSlotIds: [],
        sourceCardName: ctx.card.name,
      },
    };
  },
};
